/* SpeedyReorder account sync: links My Printer List to the customer's account
   via the Customer Account API, so it follows them across devices when signed in.
   Guests are untouched - everything here is additive on top of assets/speedy-reorder.js,
   which still works purely from localStorage on its own. */
window.SpeedyReorderAccount = (function () {
  var CLIENT_ID = '3e35fc8f-1a8f-4d81-bb6a-846438450b85';
  var REDIRECT_URI = 'https://myinktoner.co.uk/pages/account-callback';
  /* Endpoints confirmed against this shop's own OAuth discovery documents:
     https://account.myinktoner.co.uk/.well-known/openid-configuration
     https://account.myinktoner.co.uk/.well-known/customer-account-api
     The token endpoint doesn't send CORS headers (by design - token exchange
     isn't meant to be callable directly from browser JS), so token requests
     go through a small Cloudflare Worker that relays them server-to-server. */
  var AUTH_ENDPOINT = 'https://account.myinktoner.co.uk/authentication/oauth/authorize';
  var TOKEN_PROXY_ENDPOINT = 'https://speedy-reorder-token-proxy.myinkntoner.workers.dev/';
  var API_ENDPOINT = 'https://account.myinktoner.co.uk/customer/api/2026-07/graphql';

  var TOKENS_KEY = 'sr_account_tokens';
  var VERIFIER_KEY = 'sr_oauth_verifier';
  var STATE_KEY = 'sr_oauth_state';
  var RETURN_TO_KEY = 'sr_oauth_return_to';
  var DEBUG_KEY = 'sr_account_debug';

  var linked = false;
  var suppressNextPush = false;
  var pushDebounceTimer = null;

  /* Temporary diagnostics: surfaces the last sync failure on the next page
     load (read by sections/my-printer-hub.liquid) instead of failing silently. */
  function setDebug(message) {
    try { localStorage.setItem(DEBUG_KEY, String(message).slice(0, 600)); } catch (e) {}
  }

  function base64UrlEncode(buffer) {
    var bytes = new Uint8Array(buffer);
    var str = '';
    for (var i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function randomToken() {
    var array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array.buffer);
  }

  function sha256Base64Url(value) {
    var data = new TextEncoder().encode(value);
    return crypto.subtle.digest('SHA-256', data).then(base64UrlEncode);
  }

  function getTokens() {
    try {
      var raw = localStorage.getItem(TOKENS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function storeTokens(tokenData) {
    try {
      localStorage.setItem(TOKENS_KEY, JSON.stringify({
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token,
        expires_at: Date.now() + (tokenData.expires_in * 1000) - 60000
      }));
    } catch (e) {}
  }

  function login() {
    var verifier = randomToken();
    var state = randomToken();
    sha256Base64Url(verifier)
      .then(function (challenge) {
        sessionStorage.setItem(VERIFIER_KEY, verifier);
        sessionStorage.setItem(STATE_KEY, state);
        sessionStorage.setItem(RETURN_TO_KEY, window.location.href);
        var params = new URLSearchParams({
          client_id: CLIENT_ID,
          response_type: 'code',
          redirect_uri: REDIRECT_URI,
          scope: 'openid email customer-account-api:full',
          state: state,
          nonce: state,
          code_challenge: challenge,
          code_challenge_method: 'S256'
        });
        window.location.href = AUTH_ENDPOINT + '?' + params.toString();
      })
      .catch(function (err) {
        setDebug('login() failed before redirect: ' + (err && err.message ? err.message : err));
      });
  }

  function logout() {
    localStorage.removeItem(TOKENS_KEY);
    linked = false;
  }

  function isLinked() {
    return linked;
  }

  function handleCallback() {
    var params = new URLSearchParams(window.location.search);
    var code = params.get('code');
    var state = params.get('state');
    var savedState = sessionStorage.getItem(STATE_KEY);
    var verifier = sessionStorage.getItem(VERIFIER_KEY);
    var returnTo = sessionStorage.getItem(RETURN_TO_KEY) || '/pages/my-printer-hub';

    function cleanupAndReturn() {
      sessionStorage.removeItem(VERIFIER_KEY);
      sessionStorage.removeItem(STATE_KEY);
      sessionStorage.removeItem(RETURN_TO_KEY);
      window.location.href = returnTo;
    }

    if (!code || !state || state !== savedState || !verifier) {
      setDebug(
        'handleCallback: missing/mismatched OAuth params. code=' + !!code +
          ' state=' + !!state + ' stateMatches=' + (state === savedState) + ' verifier=' + !!verifier
      );
      cleanupAndReturn();
      return;
    }

    fetch(TOKEN_PROXY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'authorization_code',
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        code: code,
        code_verifier: verifier
      })
    })
      .then(function (res) {
        return res.text().then(function (text) {
          var json = null;
          try { json = JSON.parse(text); } catch (e) {}
          if (!res.ok || !json || !json.access_token) {
            setDebug('Token exchange failed (HTTP ' + res.status + '): ' + text);
          }
          return json;
        });
      })
      .then(function (tokenData) {
        if (tokenData && tokenData.access_token) {
          storeTokens(tokenData);
        }
      })
      .catch(function (err) {
        setDebug('Token exchange request threw: ' + (err && err.message ? err.message : err));
      })
      .then(cleanupAndReturn);
  }

  function getValidAccessToken() {
    var tokens = getTokens();
    if (!tokens) return Promise.resolve(null);
    if (tokens.expires_at > Date.now()) return Promise.resolve(tokens.access_token);
    if (!tokens.refresh_token) return Promise.resolve(null);

    return fetch(TOKEN_PROXY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        client_id: CLIENT_ID,
        refresh_token: tokens.refresh_token
      })
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (tokenData) {
        if (tokenData && tokenData.access_token) {
          storeTokens(tokenData);
          return tokenData.access_token;
        }
        setDebug('Refresh token exchange returned no access_token: ' + JSON.stringify(tokenData));
        localStorage.removeItem(TOKENS_KEY);
        return null;
      })
      .catch(function (err) {
        setDebug('Refresh token request threw: ' + (err && err.message ? err.message : err));
        return null;
      });
  }

  function customerAccountQuery(query, variables) {
    return getValidAccessToken().then(function (token) {
      if (!token) return null;
      return fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token
        },
        body: JSON.stringify({ query: query, variables: variables || {} })
      })
        .then(function (res) { return res.json().then(function (body) { return { ok: res.ok, body: body }; }); })
        .catch(function () { return { ok: false, body: null }; });
    });
  }

  function pullAccountData() {
    var query = 'query { customer { id metafield(namespace: "speedy_reorder", key: "data") { value } } }';
    return customerAccountQuery(query).then(function (result) {
      if (!result) {
        /* No stored token yet - this is the normal state before the customer
           has ever clicked "Sign in & sync", so it's not worth surfacing as an error. */
        return { customerId: null, data: null, errors: null };
      }
      if (!result.ok || !result.body || !result.body.data || !result.body.data.customer) {
        setDebug('pullAccountData failed: ' + JSON.stringify(result.body));
        return { customerId: null, data: null, errors: result.body && result.body.errors };
      }
      var customer = result.body.data.customer;
      var data = null;
      if (customer.metafield && customer.metafield.value) {
        try {
          data = JSON.parse(customer.metafield.value);
        } catch (e) {}
      }
      return { customerId: customer.id, data: data, errors: null };
    });
  }

  function pushAccountData(customerId, data) {
    if (!customerId) return Promise.resolve(null);
    var mutation =
      'mutation SpeedyReorderSetData($metafields: [MetafieldsSetInput!]!) { metafieldsSet(metafields: $metafields) { metafields { id } userErrors { field message } } }';
    var variables = {
      metafields: [
        {
          ownerId: customerId,
          namespace: 'speedy_reorder',
          key: 'data',
          type: 'json',
          value: JSON.stringify(data)
        }
      ]
    };
    return customerAccountQuery(mutation, variables).then(function (result) {
      if (!result || !result.ok) {
        setDebug('pushAccountData failed: ' + JSON.stringify(result && result.body));
      } else if (result.body && result.body.data && result.body.data.metafieldsSet && result.body.data.metafieldsSet.userErrors && result.body.data.metafieldsSet.userErrors.length) {
        setDebug('pushAccountData userErrors: ' + JSON.stringify(result.body.data.metafieldsSet.userErrors));
      }
      return result;
    });
  }

  /* Merges the account's saved fleet with whatever is already on this device,
     matching entries by printer (collectionHandle) so a printer added locally
     just before the first sync is never silently discarded - and any cartridges
     saved on one side but not the other end up on both. */
  function mergeFleets(remoteFleet, localFleet) {
    var merged = remoteFleet.map(function (entry) {
      return {
        id: entry.id,
        label: entry.label,
        collectionHandle: entry.collectionHandle,
        collectionTitle: entry.collectionTitle,
        products: (entry.products || []).slice()
      };
    });

    localFleet.forEach(function (localEntry) {
      var match = null;
      for (var i = 0; i < merged.length; i++) {
        if (merged[i].collectionHandle === localEntry.collectionHandle) {
          match = merged[i];
          break;
        }
      }
      if (match) {
        (localEntry.products || []).forEach(function (p) {
          var already = match.products.some(function (mp) { return mp.variantId === p.variantId; });
          if (!already) match.products.push(p);
        });
      } else {
        merged.push(localEntry);
      }
    });

    return merged;
  }

  var currentCustomerId = null;

  function autoSync() {
    if (!window.SpeedyReorder) return;

    pullAccountData().then(function (result) {
      if (!result || !result.customerId) {
        linked = false;
        return;
      }
      linked = true;
      currentCustomerId = result.customerId;

      var localFleet = window.SpeedyReorder.getFleet();
      var localLocked = window.SpeedyReorder.isListLocked();

      if (result.data) {
        var remoteFleet = result.data.fleet || [];
        var remoteLocked = typeof result.data.locked === 'boolean' ? result.data.locked : localLocked;

        if (localFleet.length) {
          var mergedData = { fleet: mergeFleets(remoteFleet, localFleet), locked: remoteLocked };
          suppressNextPush = true;
          window.SpeedyReorder.importAccountData(mergedData);
          pushAccountData(currentCustomerId, mergedData);
        } else {
          suppressNextPush = true;
          window.SpeedyReorder.importAccountData(result.data);
        }
      } else if (localFleet.length) {
        pushAccountData(currentCustomerId, { fleet: localFleet, locked: localLocked });
      }

      document.dispatchEvent(new CustomEvent('speedyreorder:account-linked'));
    });
  }

  document.addEventListener('DOMContentLoaded', autoSync);

  document.addEventListener('speedyreorder:fleet-updated', function () {
    if (suppressNextPush) {
      suppressNextPush = false;
      return;
    }
    if (!linked || !currentCustomerId || !window.SpeedyReorder) return;
    window.clearTimeout(pushDebounceTimer);
    pushDebounceTimer = window.setTimeout(function () {
      pushAccountData(currentCustomerId, {
        fleet: window.SpeedyReorder.getFleet(),
        locked: window.SpeedyReorder.isListLocked()
      });
    }, 800);
  });

  return {
    login: login,
    logout: logout,
    isLinked: isLinked,
    handleCallback: handleCallback
  };
})();
